import { useState } from 'react';
import { Send, Edit, X, User, Building, Briefcase, Clock, AlertTriangle } from 'lucide-react';
import DraftEditor from './DraftEditor';

export default function DraftReviewCard({ draft, isSelected, onSelect, onApprove, onReject, onEdit }) {
  const [showEditor, setShowEditor] = useState(false);
  const scores = draft.scores_json || {};
  const analysis = draft.analysis_json || {};

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  const meetsThresholds = 
    scores.humanism >= 80 &&
    scores.professionalism >= 85 &&
    scores.non_predatory >= 85 &&
    scores.relevance >= 90;

  return (
    <>
      <div
        className={`bg-white rounded-lg shadow p-6 cursor-pointer border-2 transition-all ${
          isSelected ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200 hover:border-purple-300'
        }`}
        onClick={onSelect}
      >
        {/* Lead Info */}
        <div className="mb-4 pb-4 border-b">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {draft.first_name} {draft.last_name}
              </h3>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                {draft.company_name && (
                  <span className="flex items-center gap-1">
                    <Building size={14} />
                    {draft.company_name}
                  </span>
                )}
                {draft.job_title && (
                  <span className="flex items-center gap-1">
                    <Briefcase size={14} />
                    {draft.job_title}
                  </span>
                )}
              </div>
            </div>
            {!meetsThresholds && (
              <AlertTriangle size={20} className="text-yellow-600" />
            )}
          </div>
        </div>

        {/* Original Reply Preview */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Original Reply:</h4>
          <div className="bg-gray-50 rounded p-3 text-sm text-gray-700 max-h-32 overflow-y-auto">
            {draft.original_reply_text?.substring(0, 200) || draft.original_reply?.substring(0, 200) || 'No reply content'}
            {(draft.original_reply_text?.length > 200 || draft.original_reply?.length > 200) && '...'}
          </div>
        </div>

        {/* AI Draft Preview */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">AI Draft:</h4>
          <div className="bg-purple-50 rounded p-3 text-sm text-gray-800 max-h-40 overflow-y-auto">
            {draft.ai_draft?.substring(0, 300) || 'No draft content'}
            {draft.ai_draft?.length > 300 && '...'}
          </div>
        </div>

        {/* Scores */}
        <div className="mb-4 grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-gray-600">Humanism:</span>
            <span className={`ml-1 font-semibold ${getScoreColor(scores.humanism)}`}>
              {scores.humanism || 0}/100
            </span>
          </div>
          <div>
            <span className="text-gray-600">Professionalism:</span>
            <span className={`ml-1 font-semibold ${getScoreColor(scores.professionalism)}`}>
              {scores.professionalism || 0}/100
            </span>
          </div>
          <div>
            <span className="text-gray-600">Non-predatory:</span>
            <span className={`ml-1 font-semibold ${getScoreColor(scores.non_predatory)}`}>
              {scores.non_predatory || 0}/100
            </span>
          </div>
          <div>
            <span className="text-gray-600">Relevance:</span>
            <span className={`ml-1 font-semibold ${getScoreColor(scores.relevance)}`}>
              {scores.relevance || 0}/100
            </span>
          </div>
        </div>

        {/* Analysis Info */}
        {analysis.category && (
          <div className="mb-4 text-xs text-gray-600">
            <span className="font-medium">Category:</span> {analysis.category} | 
            <span className="ml-1 font-medium">Sentiment:</span> {analysis.sentiment}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-4 border-t" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onApprove();
            }}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center gap-2 text-sm font-medium"
          >
            <Send size={16} />
            Send as-is
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowEditor(true);
            }}
            className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 flex items-center justify-center gap-2 text-sm font-medium"
          >
            <Edit size={16} />
            Edit
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReject();
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center justify-center gap-2 text-sm font-medium"
          >
            <X size={16} />
            Reject
          </button>
        </div>

        {/* Keyboard Shortcuts Hint */}
        <div className="mt-2 text-xs text-gray-500 text-center">
          Press <kbd className="px-1 py-0.5 bg-gray-100 rounded">Enter</kbd> to send, <kbd className="px-1 py-0.5 bg-gray-100 rounded">E</kbd> to edit, <kbd className="px-1 py-0.5 bg-gray-100 rounded">Esc</kbd> to close
        </div>
      </div>

      {/* Draft Editor Modal */}
      {showEditor && (
        <DraftEditor
          draft={draft}
          onSave={(editedDraft) => {
            onEdit(editedDraft);
            setShowEditor(false);
          }}
          onCancel={() => setShowEditor(false)}
        />
      )}
    </>
  );
}


